import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointInvestmentGoalsData,
  type EnrollmentJointInvestmentGoalsSubmitInput,
  type EnrollmentJointInvestmentGoalsSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint Investment Goals data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointInvestmentGoalsData>
 * @example const response = await getEnrollmentJointInvestmentGoalsData()
 */
export const getEnrollmentJointInvestmentGoalsData = async (
  context?: DefaultContext,
): Promise<EnrollmentJointInvestmentGoalsData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointInvestmentGoalsData',
      query: `mutation getEnrollmentJointInvestmentGoalsData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            applicationId
            path
            progress
            investmentObjective
            investmentExperience
            name {
              firstName
              lastName
            }
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointInvestmentGoalsData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointInvestmentGoalsData = createQueryHook<undefined, EnrollmentJointInvestmentGoalsData>(
  getEnrollmentJointInvestmentGoalsData,
)

/**
 * Submit Enrollment Joint Investment Goals data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointInvestmentGoalsSubmitOutput>
 * @example const response = await submitEnrollmentJointInvestmentGoalsData();
 */
export const submitEnrollmentJointInvestmentGoalsData = async (
  enrollmentInvestmentGoalsSubmitInput: EnrollmentJointInvestmentGoalsSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointInvestmentGoalsSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointInvestmentGoalsData',
      query: `mutation submitEnrollmentJointInvestmentGoalsData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentInvestmentGoalsSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointInvestmentGoalsSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointInvestmentGoalsData = createMutationHook<
  EnrollmentJointInvestmentGoalsSubmitInput,
  EnrollmentJointInvestmentGoalsSubmitOutput
>(submitEnrollmentJointInvestmentGoalsData)
